import * as ModelEvent from "@node-elion/syncron";
import { uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNames } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import SubscriptionBase from "../../types/Subscription";
import {
	Executor,
	TaxiService,
	Base,
	Language,
	ExecutorRateSubscription,
	ExecutorRateCommissionPlan,
	ExecutorRateDiscountPlan,
	CarParkSettings,
} from "..";

const conditionOperatorMaps = {
	fromResponse: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterEqual: "greaterOrEqual",
		lessEqual: "lessOrEqual",
	},
	toRequest: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterOrEqual: "greaterEqual",
		lessOrEqual: "lessEqual",
	},
} as const;

const logger = createLogger({ name: "ExecutorRate" });

class ExecutorRate extends Base {
	static fromResponse(data: any, lang: Language = "uk"): ExecutorRate.Model {
		const taxiServiceIds = ({
			taxiServices = [],
		}: ExecutorRate.Model): number[] => {
			try {
				if (!taxiServices || !taxiServices.length) return [];
				return uniq<number>(taxiServices.map((item) => item?.id));
			} catch {
				return [];
			}
		};
		const subscriptions = data?.subscriptions?.map(
			ExecutorRateSubscription.fromResponse,
		);

		const commissionPlans = data?.commissionPlans?.map(
			ExecutorRateCommissionPlan.fromResponse,
		);

		const discountPlans = data?.discountedPlans?.map(
			ExecutorRateDiscountPlan.fromResponse,
		);

		return {
			subscriptions,
			commissionPlans,
			discountPlans,
			id: data?.id,
			// active: data.active,
			active: true,
			default: data?.default,
			root: data?.root || false,

			startDate: data?.startDate,
			startTime: data?.startTime,

			executorIds: data?.executorIds,

			taxiServices: data?.taxiServices || [],
			taxiServiceIds: taxiServiceIds(data),

			name: data?.name?.[lang] || data?.name?.ru || "",

			ignoreOldOrders:
				data?.configuration?.discounted?.ignoreOldOrders ?? false,

			amountOfMoneyToBlock: {
				active:
					data?.configuration?.locking?.lockingAmount?.active ??
					false,
				value: data?.configuration?.locking?.lockingAmount?.value ?? 0,
				auto:
					data?.configuration?.locking?.lockingAmount?.auto ?? false,
			},

			amountOfMoneyToUnblock: {
				active:
					data?.configuration?.locking?.unlockingAmount?.active ??
					false,
				value:
					data?.configuration?.locking?.unlockingAmount?.value ?? 0,
				auto:
					data?.configuration?.locking?.unlockingAmount?.auto ??
					false,
			},

			orderTypeCondition: {
				active:
					data?.configuration?.locking?.conditions?.active ?? false,
				operator: data?.configuration?.locking?.conditions?.direction
					? conditionOperatorMaps.fromResponse[
							data?.configuration?.locking?.conditions?.direction
					  ]
					: "equal",
				value: data?.configuration?.locking?.conditions?.value ?? 0,
				types: data?.configuration?.locking?.conditions?.types ?? [
					"cashOrders",
				],
			},

			disallowTakeOrders: {
				active:
					data?.configuration?.locking?.conditions?.orderRestrictions
						?.active ?? false,
				value:
					data?.configuration?.locking?.conditions?.orderRestrictions
						?.commissionThreshold ?? 0,
			},

			updatedAt: data?.updatedAt,
			createdAt: data?.createdAt,
			deletedAt: data?.deletedAt,
		};
	}

	static toRequest(
		model: ExecutorRate.Model.New | ExecutorRate.Model.Modified,
	): any {
		const commissionPlans = model.commissionPlans?.map(
			(commissionPlan) => ({
				...ExecutorRateCommissionPlan.toRequest(commissionPlan),

				id: commissionPlan.id,
			}),
		);

		const payload = {
			startDate: model.startDate,
			startTime: model.startTime,
			active: true,
			default: model.default,
			taxiServiceIds: model.taxiServiceIds,

			executorIds: model.executorIds,

			subscriptions: model.subscriptions?.map((subscription) => ({
				...ExecutorRateSubscription.toRequest(subscription),

				id: subscription.id,
			})),

			commissionPlans,

			discountedPlans: model.discountPlans?.map((discountPlan) => ({
				...ExecutorRateDiscountPlan.toRequest(discountPlan),

				id: discountPlan.id,
			})),
			name: createObjectLanguageNames(model.name),

			configuration: {
				discounted: {
					ignoreOldOrders: model.ignoreOldOrders,
				},

				locking: {
					lockingAmount: {
						active: model.amountOfMoneyToBlock?.active,
						value: model.amountOfMoneyToBlock?.value,
						auto: model.amountOfMoneyToBlock?.auto,
					},

					unlockingAmount: {
						active: model.amountOfMoneyToUnblock?.active,
						value: model.amountOfMoneyToUnblock?.value,
						auto: model.amountOfMoneyToUnblock?.auto,
					},

					conditions: {
						active: model.orderTypeCondition?.active,
						value: model.orderTypeCondition?.value,
						types: model.orderTypeCondition?.types,
						direction: model.orderTypeCondition
							? conditionOperatorMaps.toRequest[
									model.orderTypeCondition.operator
							  ]
							: undefined,

						orderRestrictions: {
							active: model.disallowTakeOrders?.active,
							commissionThreshold:
								model.disallowTakeOrders?.value,
						},
					},
				},
			},
		};

		return payload;
	}

	public static async getById(id: number, lang?: Language) {
		const data = await this.request((prpc) =>
			prpc.theirsModel.executorRate.getById(id),
		);

		return ExecutorRate.fromResponse(data, lang);
	}

	public static async store(object: ExecutorRate.Model.New, force = false) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorRate.create(
						ExecutorRate.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorRate store", { object, res, force });
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async update(
		object: ExecutorRate.Model.Modified,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorRate.update(
						object.id,
						ExecutorRate.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorRate update", {
				format: ExecutorRate.toRequest(object),
				object,
				res,
				force,
			});
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async destroy(id: number[] | number) {
		if (Array.isArray(id))
			await Promise.all(id.map((id) => this.destroyOne(id)));
		else await this.destroyOne(id);
	}

	public static async getHistory(
		params: ExecutorRate.History.GetListParams,
	): Promise<ExecutorRate.History.GetListResponse> {
		const empty: ExecutorRate.History.GetListResponse = {
			items: [],
			pagination: { limit: null, offset: 0, count: 0 },
		};
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.executorRate.getHistory(params),
				{ silent: false, error: true },
			);
			logger.info("[ExecutorRate] getHistory", { params, res });
			return res ?? empty;
		} catch (error) {
			logger.error("[ExecutorRate] getHistory error", error);
			return empty;
		}
	}

	public static async getHistoryItem(
		params: ExecutorRate.History.GetItemParams,
	): Promise<ExecutorRate.History.ExpandedItem | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.executorRate.getHistoryItem(params),
				{ silent: false, error: true },
			);
			logger.info("[ExecutorRate] getHistoryItem", { params, res });
			return res ?? null;
		} catch (error) {
			logger.error("[ExecutorRate] getHistoryItem error", error);
			return null;
		}
	}

	public static async cancelPending(
		params: ExecutorRate.CancelPending.Params,
	): Promise<ExecutorRate.CancelPending.Response> {
		const empty: ExecutorRate.CancelPending.Response = {
			cancelledExecutorIds: [],
		};
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.executorRate.cancelPending(params),
				{ silent: false, error: true },
			);
			logger.info("[ExecutorRate] cancelPending", { params, res });
			return res ?? empty;
		} catch (error) {
			logger.error("[ExecutorRate] cancelPending error", error);
			return empty;
		}
	}

	public static async schedule(
		params: ExecutorRate.Schedule.Params,
	): Promise<ExecutorRate.Schedule.Response | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.executorRate.schedule(params),
				{ silent: false, error: true },
			);
			logger.info("[ExecutorRate] schedule", { params, res });
			return res ?? null;
		} catch (error) {
			logger.error("[ExecutorRate] schedule error", error);
			return null;
		}
	}

	public static async subscribe(
		options: ExecutorRate.SubscribeOptions,
		onUpdate: SubscriptionBase.OnUpdate<ExecutorRate.Model>,
	): Promise<SubscriptionBase<ExecutorRate.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[ExecutorRate] subscribe", { options, state });
				onUpdate({
					...state,

					models: state.models.map((item) =>
						this.fromResponse(item, options.lang),
					),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorRate.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (event) => {
						modelEventConstructor.onEvent(event);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "ExecutorRate.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: ExecutorRate.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(options)),
		} as SubscriptionBase<ExecutorRate.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.executorRate.delete(id));
	}

	private static optionsToRequest(options: ExecutorRate.SubscribeOptions) {
		return {
			query: options.query,
			offset: options.offset,
			limit: options.limit,

			taxiServiceIds: options.taxiServiceIds,

			order: options.order,
		};
	}
}

declare namespace ExecutorRate {
	export interface Model {
		id: number;
		active: boolean;
		root: boolean;
		default: boolean;

		startDate: string;
		startTime: string;

		executorIds: number[];

		taxiServices?: TaxiService.Model[];
		taxiServiceIds: number[];
		executors?: Executor.Model[];

		subscriptions?: ExecutorRateSubscription.Model[];
		commissionPlans?: ExecutorRateCommissionPlan.Model[];
		discountPlans?: ExecutorRateDiscountPlan.Model[];

		name: string;

		ignoreOldOrders: boolean;
		settingsCarPark?: CarParkSettings.Model[]; //* use only front

		amountOfMoneyToBlock: {
			active: boolean;
			value: number;
			auto: boolean;
		};
		amountOfMoneyToUnblock: {
			active: boolean;
			value: number;
			auto: boolean;
		};

		orderTypeCondition: {
			active: boolean;
			operator:
				| "greater"
				| "less"
				| "equal"
				| "greaterOrEqual"
				| "lessOrEqual";
			value: number;
			types: (
				| "cashOrders"
				| "nonCashOrders"
				| "suburbanOrders"
				| "onlineOrders"
				| "bonusOrders"
				| "executorOrder"
				| "cardPaymentToTaxiService"
				| "cardPaymentToExecutor"
				| "terminalToTaxiService"
				| "terminalToExecutor"
				| "localTransfer"
				| "globalTransfer"
				| "externalTransfer"
			)[];
		};
		disallowTakeOrders: {
			active: boolean;
			value: number;
		};

		updatedAt: string;
		createdAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<ServiceSubscribeOptionsBase<ExecutorRate.Model>, "order"> {
		taxiServiceIds?: number[];
		lang?: Language;
		order?: Order;
	}

	type Order = Partial<Record<keyof Model, SortingOrder>>;

	namespace Model {
		interface New {
			name: string;

			startDate: string;
			startTime: string;

			active: boolean;
			default: boolean;
			taxiServiceIds: number[];
			executorIds: number[];

			subscriptions: ((
				| ExecutorRateSubscription.Model.New
				| ExecutorRateSubscription.Model.Modified
			) & { id?: number })[];

			commissionPlans: ((
				| ExecutorRateCommissionPlan.Model.New
				| ExecutorRateCommissionPlan.Model.Modified
			) & { id?: number })[];

			discountPlans: ((
				| ExecutorRateDiscountPlan.Model.New
				| ExecutorRateDiscountPlan.Model.Modified
			) & { id?: number })[];

			ignoreOldOrders: boolean;

			amountOfMoneyToBlock: {
				active: boolean;
				value: number;
				auto: boolean;
			};
			amountOfMoneyToUnblock: {
				active: boolean;
				value: number;
				auto: boolean;
			};

			orderTypeCondition: {
				active: boolean;
				operator:
					| "greater"
					| "less"
					| "equal"
					| "greaterOrEqual"
					| "lessOrEqual";
				value: number;
				types: (
					| "cashOrders"
					| "nonCashOrders"
					| "suburbanOrders"
					| "onlineOrders"
					| "bonusOrders"
					| "executorOrder"
					| "cardPaymentToTaxiService"
					| "cardPaymentToExecutor"
					| "terminalToTaxiService"
					| "terminalToExecutor"
					| "localTransfer"
					| "globalTransfer"
					| "externalTransfer"
				)[];
			};
			disallowTakeOrders: {
				active: boolean;
				value: number;
			};
		}
		type Modified = Partial<New> & { id: number };
	}

	export type HistoryType =
		| "executor"
		| "rate"
		| "commissionPlan"
		| "subscriptionPlan"
		| "discountedPlan";

	export namespace History {
		export interface Pagination {
			limit: number | null;
			offset: number;
			count: number;
		}

		export interface GetListParams {
			id: number;
			type: HistoryType;
			limit?: number;
			offset?: number;
		}

		export interface GetItemParams {
			id: number;
			type: HistoryType;
		}

		export interface RateRef {
			id: number;
			historyId: number;
			name: Record<Language, string>;
		}

		export interface PlanSummary {
			id: number;
			planId: number | null;
			name: Record<Language, string>;
			createdAt: string;
		}

		export interface RatePlansSummary {
			commissionPlans: PlanSummary[];
			subscriptionPlans: PlanSummary[];
			discountedPlans: PlanSummary[];
		}

		export interface ExecutorTransitionItem {
			id: number;
			executorId: number;
			executorRateHistoryId: number;
			startedAt: string;
			endedAt: string | null;
			deletedAt: string | null;
			rate: RateRef | null;
		}

		export interface RateHistoryListItem {
			id: number;
			v: number;
			name: Record<Language, string>;
			active: boolean;
			root: boolean;
			default: boolean;
			startDate: string | null;
			startTime: string | null;
			configuration?: any;
			taxiServiceIds: number[];
			createdAt: string;
			executorRateId: number;
			plans: RatePlansSummary;
		}

		export interface PlanHistoryListItem {
			id: number;
			v: number;
			name: Record<Language, string>;
			createdAt: string;
			executorRateHistoryId: number;
			commissionPlanId?: number | null;
			commissionPlanHistoryId?: number | null;
			subscriptionPlanId?: number | null;
			discountedPlanId?: number | null;
			classifierId?: number | null;
			active?: boolean;
			startDate?: string;
			configuration?: any;
			action?: string;
			weekdays?: number[];
			periodValue?: number;
			period?: string;
			ignoreOldOrders?: boolean;
			threshold?: number;
			commissionPlanHistory?: {
				id: number;
				name: Record<Language, string>;
			} | null;
		}

		export type ListItem =
			| ExecutorTransitionItem
			| RateHistoryListItem
			| PlanHistoryListItem;

		export interface GetListResponse {
			items: ListItem[];
			pagination: Pagination;
		}

		export interface RateHistoryExpanded {
			id: number;
			v: number;
			name: Record<Language, string>;
			active: boolean;
			root: boolean;
			default: boolean;
			startDate: string | null;
			startTime: string | null;
			configuration?: unknown;
			taxiServiceIds: number[];
			createdAt: string;
			executorRateId: number;
			commissionPlanHistories?: PlanHistoryListItem[];
			subscriptionPlanHistories?: PlanHistoryListItem[];
			discountedPlanHistories?: PlanHistoryListItem[];
		}

		export interface ExecutorTransitionExpanded {
			id: number;
			executorId: number;
			executorRateHistoryId: number;
			startedAt: string;
			endedAt: string | null;
			deletedAt: string | null;
			executorRateHistory?: RateHistoryExpanded | null;
		}

		export interface PlanHistoryExpanded extends PlanHistoryListItem {
			executorRateHistory?: RateHistoryExpanded | null;
			commissionPlanHistory?: PlanHistoryListItem | null;
		}

		export type ExpandedItem =
			| ExecutorTransitionExpanded
			| RateHistoryExpanded
			| PlanHistoryExpanded;
	}

	export namespace CancelPending {
		export interface Params {
			executorId?: number;
			executorIds?: number[];
			nextRateId?: number;
		}

		export interface Response {
			cancelledExecutorIds: number[];
		}
	}

	export namespace Schedule {
		export interface Params {
			executorId: number;
			executorRateId: number;
			time?: Date | string | number;
			startDate?: string | Date | null;
			startTime?: string | null;
		}

		export interface Response {
			appliedImmediately: boolean;
			executorId: number;
			executorRateId: number;
			startsAt: string;
		}
	}
}

export default ExecutorRate;
